import {
    GetFundingRequirementsCreditFacilityResponse,
    GetStateResponse,
    GetContextResponse
} from '@iwoca/lapi-client/edge';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
    TDeserialisedPeople,
    TDeserialisedPerson,
    TResolutionMethodsSerialisedStatuses,
    TSerialisedPeople
} from './IdCheckVerificationModal.types';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';

export function buildOnfidoMessagesSent(
    previousState: string[] | null,
    currentUID: string
) {
    if (!previousState) {
        return [currentUID];
    }

    return _.union(previousState, [currentUID]);
}

export function deserialiseIdCheckResponse(
    customer_state: GetStateResponse['data'],
    funding_requirement: GetFundingRequirementsCreditFacilityResponse['data'],
    site_context: GetContextResponse['data']
):
    | {
          peopleToVerify: TDeserialisedPeople;
          id_verification_provider_name: 'Entrust (Onfido)' | 'IDVerse';
      }
    | undefined {
    const onfidoResolutionMethod = _.find(funding_requirement, {
        recommended_resolution_method: 'onfido_check'
    });
    const onfidoVideoResolutionMethod = _.find(funding_requirement, {
        recommended_resolution_method: 'onfido_video_check'
    });
    const idverseCheckResolutionMethod = _.find(funding_requirement, {
        recommended_resolution_method: 'idverse_check'
    });

    if (
        !onfidoResolutionMethod &&
        !onfidoVideoResolutionMethod &&
        !idverseCheckResolutionMethod
    )
        return undefined;

    const onfidoChecks = onfidoResolutionMethod?.resolution_methods;
    const onfidoStatuses = onfidoChecks
        ? _.find(onfidoChecks, { type: 'onfido_check' })?.items
        : undefined;

    const onfidoVideoChecks = onfidoVideoResolutionMethod?.resolution_methods;
    const onfidoVideoStatuses = onfidoVideoChecks
        ? _.find(onfidoVideoChecks, { type: 'onfido_video_check' })?.items
        : undefined;

    const idverseChecks = idverseCheckResolutionMethod?.resolution_methods;
    const idverseStatuses = idverseChecks
        ? _.find(idverseChecks, { type: 'idverse_check' })?.items
        : undefined;

    const isAccessUser = site_context?.logged_in_user_type === 'access_user';

    const isPgConsentSplitTestEnabled =
        hasNonApplicantPgConsentSplitTest(site_context);

    const onlyApplicantsGuarantors = _.chain(customer_state.application!.people)
        .filter((person) => {
            const roles = person?.roles ?? [];
            const isApplicant = roles.indexOf('applicant') > -1;
            const isGuarantor = roles.indexOf('guarantor') > -1;

            if (isPgConsentSplitTestEnabled) {
                return isAccessUser ? isGuarantor : isApplicant;
            }

            return isApplicant || isGuarantor;
        })
        .value();

    const id_verification_provider_name = hasIDVerseSplitTest(site_context)
        ? 'IDVerse'
        : 'Entrust (Onfido)';

    return {
        peopleToVerify: deserialisePeople(
            onlyApplicantsGuarantors,
            onfidoStatuses,
            onfidoVideoStatuses,
            idverseStatuses
        ),
        id_verification_provider_name
    };
}

export function serialisePersonState(
    people: TDeserialisedPeople,
    selectedPerson: TDeserialisedPerson
) {
    return _.map(people, (person) => {
        if (person.uid === selectedPerson.uid) {
            if (person.phones) {
                person.phones.push({
                    number: selectedPerson.newPhoneNumber!,
                    uid: uuidv4(),
                    type: 'secondary'
                });
            } else {
                person.phones = [
                    {
                        number: selectedPerson.newPhoneNumber,
                        uid: uuidv4(),
                        type: 'primary'
                    }
                ];
            }
        }
        return person;
    });
}

function deserialisePeople(
    people: TSerialisedPeople,
    onfidoStatuses: TResolutionMethodsSerialisedStatuses,
    onfidoVideoStatuses: TResolutionMethodsSerialisedStatuses,
    idverseStatuses: TResolutionMethodsSerialisedStatuses
): TDeserialisedPeople {
    return _.map(people, (person) => {
        return {
            uid: person.uid,
            title: person?.title!,
            firstName: person?.first_name!,
            lastName: person?.last_name!,
            roles: person?.roles,
            phones: deserialisePhones(person.phones),
            idCheckStatus: getIdCheckStatus(
                onfidoStatuses,
                onfidoVideoStatuses,
                idverseStatuses,
                person.uid
            ),
            addPhoneNumberPending: false
        };
    });
}

export function getIdCheckStatus(
    onfidoStatusList: TResolutionMethodsSerialisedStatuses,
    onfidoVideoStatusList: TResolutionMethodsSerialisedStatuses,
    idverseStatusList: TResolutionMethodsSerialisedStatuses,
    uid: string
) {
    const onfidoStatus = _.find(onfidoStatusList, { name: uid });
    const onfidoVideoStatus = _.find(onfidoVideoStatusList, { name: uid });
    const idverseStatus = _.find(idverseStatusList, { name: uid });

    if (onfidoStatus || idverseStatus) {
        if (
            (onfidoStatus?.status === 'completed' &&
                (onfidoVideoStatus?.status ?? 'completed') === 'completed') ||
            idverseStatus?.status === 'completed'
        ) {
            return 'Verified';
        }
        if (
            onfidoStatus?.status === 'pending' ||
            onfidoVideoStatus?.status === 'pending' ||
            idverseStatus?.status === 'pending'
        ) {
            return 'Verifying your check';
        }
        if (
            onfidoStatus?.status === 'error' ||
            onfidoVideoStatus?.status === 'error' ||
            idverseStatus?.status === 'error'
        ) {
            return 'Something went wrong';
        }
        return 'Not started yet';
    } else {
        return 'Something went wrong';
    }
}

function deserialisePhones(
    phones: NonNullable<TSerialisedPeople>[number]['phones']
) {
    const deserialisedPhones: {
        uid?: string;
        label?: string;
        value?: string | null;
        type?: 'primary' | 'secondary' | 'ADD_NEW';
    }[] = _.map(phones, (phone) => {
        return {
            uid: phone.uid,
            label: phone.number,
            value: phone.number
        };
    });

    deserialisedPhones.push({
        type: 'ADD_NEW',
        label: 'Add new',
        value: null
    });

    return deserialisedPhones;
}

export function toggleAddPhoneNumber(
    people: TDeserialisedPeople,
    personUID: string
) {
    const peopleToVerify = _.cloneDeep(people);

    _.map(peopleToVerify, (person) => {
        if (person!.uid === personUID) {
            _.extend(person, { addPhoneToggle: !person!.addPhoneToggle });
        }
    });

    return peopleToVerify;
}

export function updatePeopleState(
    people: TDeserialisedPeople,
    personUID: string,
    overrideObj: {
        newPhoneNumber?: string;
        error?: boolean;
        SMSPending?: boolean;
        status?: string;
        addPhoneNumberPending?: boolean;
    }
) {
    const peopleToVerify = _.cloneDeep(people);

    _.map(peopleToVerify, (person) => {
        if (person.uid === personUID) {
            _.extend(person, overrideObj);
        }
    });

    return peopleToVerify;
}

export function isValid(phoneNumber: string) {
    if (getCountry() === 'DE') {
        return /^(0|(\+49)|0049)[0-9]{8,15}$/.test(phoneNumber);
    } else {
        return /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/.test(
            phoneNumber
        );
    }
}

export function hasIDVerseSplitTest(
    site_context: GetContextResponse['data']
): boolean {
    return Boolean(
        site_context?.user_split_tests.some((key) =>
            Boolean(
                'id_verification_provider_idverse' in key &&
                    key.id_verification_provider_idverse
            )
        )
    );
}

function hasNonApplicantPgConsentSplitTest(
    site_context: GetContextResponse['data']
): boolean {
    return Boolean(
        site_context?.user_split_tests.some((key) =>
            Boolean(
                'non_applicant_pg_consent' in key &&
                    key.non_applicant_pg_consent
            )
        )
    );
}
